import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {UserNotification, NotificationsResponse} from '../types'
import {markAsUnseen} from './seenNotifications'

type InfiniteQueryData = {
    pages: NotificationsResponse[]
    pageParams: (number | undefined)[]
}

/**
 * Helper function to update notifications in all cached queries
 */
export const updateNotificationsInCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    updateFn: (notification: UserNotification) => UserNotification
) => {
    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    data: page.data.map(updateFn)
                }))
            }
        }
    )
}

/**
 * Helper function to add a new notification to all cached queries
 */
export const addNotificationToCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    newNotification: UserNotification
) => {
    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData

            // A socket push can reference a notification already present in the cache
            // (e.g. an optimistic stub added by an earlier push, or the record already
            // loaded via REST) — match on either id to avoid rendering it twice.
            const isSameNotification = (notification: UserNotification) =>
                notification.id === newNotification.id ||
                notification.notification.id === newNotification.notification.id
            const hadExistingEntry = oldData.pages.some(page => page.data.some(isSameNotification))
            if (hadExistingEntry) {
                // New activity on an already-seen notification (e.g. a new message on a
                // conversation) must re-surface the unread dot. This never touches
                // `readAt` or the "manually marked unread" flag — see NotificationItem.
                markAsUnseen(newNotification.id)
            }

            return {
                ...oldData,
                pages: oldData.pages.map((page, index) => {
                    const data = page.data.filter(notification => !isSameNotification(notification))
                    // Only add to the first page
                    if (index === 0) {
                        return {
                            data: [newNotification, ...data],
                            meta: {
                                ...page.meta,
                                total: (page.meta.total || 0) + (hadExistingEntry ? 0 : 1)
                            }
                        }
                    }
                    return data.length === page.data.length ? page : {...page, data}
                })
            }
        }
    )
}
